import { FileUp } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import type { EditorPort, EditorSnapshot } from '@/ports/editor';

interface EditorSurfaceProps {
  readonly port: EditorPort;
  readonly snapshot: EditorSnapshot;
  readonly onSelection: (id: string | null) => void;
  readonly onTextEdit: (id: string) => void;
  readonly onEmptyStickerImport: () => void;
  readonly onFileDrop: (files: readonly File[]) => void;
}

function carriesFiles(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes('Files');
}

export function EditorSurface({ port, snapshot, onSelection, onTextEdit, onEmptyStickerImport, onFileDrop }: EditorSurfaceProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dragDepthRef = useRef(0);
  const [fileDropActive, setFileDropActive] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    return port.mountSurface(ref.current, {
      onSelect: onSelection,
      onRequestTextEdit: onTextEdit,
    });
  }, [onSelection, onTextEdit, port]);

  const product = snapshot.document?.product;
  const width = product?.kind === 'tube' ? product.developmentEffectiveMm : product?.widthMm;
  const height = product?.kind === 'tube' ? product.heightMm : product?.heightMm;
  const emptySticker = product?.kind === 'sticker'
    && !snapshot.document?.nodes.some((node) => node.type === 'image' && (node.role === 'sticker-image' || node.role === 'pdf-art'));

  const resetFileDrag = () => {
    dragDepthRef.current = 0;
    setFileDropActive(false);
  };

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setFileDropActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setFileDropActive(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!carriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    resetFileDrag();
    if (files.length > 0) onFileDrop(files);
  };

  return (
    <main
      className="editor-stage"
      aria-label="Mesa de trabajo"
      data-file-drop-active={fileDropActive ? 'true' : 'false'}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={resetFileDrag}
    >
      <div className="stage-hud" aria-live="polite">
        <span>{product?.kind === 'tube' ? `DESARROLLO ${width} mm` : `PDF real: ${width} × ${height} mm`}</span>
        {product?.kind === 'tube' ? <span>ALTO {height} mm</span> : null}
        <span>{snapshot.camera.zoomPercent}%</span>
      </div>
      <div ref={ref} className="editor-surface-mount" data-testid="editor-surface" />
      {fileDropActive ? (
        <div className="editor-file-drop" role="status" aria-live="polite">
          <FileUp aria-hidden="true" />
          <strong>Soltá la imagen o PDF</strong>
          <span>Se agregará a la mesa</span>
        </div>
      ) : null}
      {emptySticker ? (
        <button className="empty-sticker-upload" type="button" onClick={onEmptyStickerImport}>
          <strong>Subí tu imagen</strong>
          <span>Tocá la mesa para elegir un archivo</span>
        </button>
      ) : null}
    </main>
  );
}
